import { useState, useEffect } from 'react';
import './OfflineIndicator.css';

/**
 * OfflineIndicator
 *
 * A non-intrusive fixed banner that appears when the user loses network
 * connectivity and auto-hides once the connection is restored.
 *
 * Accessibility: uses role="status" + aria-live="polite" so screen readers
 * announce the change without interrupting the user.
 */
export default function OfflineIndicator() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [showRestored, setShowRestored] = useState(false);

  useEffect(() => {
    function handleOffline() {
      setIsOffline(true);
      setWasOffline(true);
      setShowRestored(false);
    }

    function handleOnline() {
      setIsOffline(false);
      if (wasOffline) {
        setShowRestored(true);
        // Hide the "back online" toast after 3 s
        const timer = setTimeout(() => {
          setShowRestored(false);
          setWasOffline(false);
        }, 3000);
        return () => clearTimeout(timer);
      }
    }

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [wasOffline]);

  if (!isOffline && !showRestored) return null;

  return (
    <div
      className={`offline-indicator ${isOffline ? 'offline-indicator--offline' : 'offline-indicator--restored'}`}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="offline-indicator__icon" aria-hidden="true">
        {isOffline ? '⚠' : '✓'}
      </span>
      <span className="offline-indicator__message">
        {isOffline
          ? 'You\'re offline — some features may be unavailable'
          : 'Back online'}
      </span>
    </div>
  );
}
